import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { leaveAPI, userAPI, attendanceAPI, compOffAPI } from "../../lib/api";
import { toast } from "sonner";
import { formatDate, capitalizeFirst } from "../../lib/utils";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Plus,
  CalendarDays,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Download,
  AlertTriangle,
  User,
  FileEdit,
  Gift,
} from "lucide-react";

const LeavesPage = () => {
  const { user, isManager, isHRAdmin, isSuperAdmin } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [balance, setBalance] = useState([]);
  const [users, setUsers] = useState([]);
  const [missingDays, setMissingDays] = useState([]);
  const [compOffs, setCompOffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("leaves"); // leaves | regularization | compoff
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [applyLoading, setApplyLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [approvalRemarks, setApprovalRemarks] = useState("");

  const [formData, setFormData] = useState({
    leave_type: "",
    start_date: "",
    end_date: "",
    reason: "",
    is_half_day: false,
  });

  // Regularization
  const [showRegDialog, setShowRegDialog] = useState(false);
  const [regData, setRegData] = useState({ date: "", resolution: "present", leave_type: "", reason: "" });
  const [regLoading, setRegLoading] = useState(false);

  // Comp-off avail
  const [showCompOffDialog, setShowCompOffDialog] = useState(false);
  const [selectedCompOff, setSelectedCompOff] = useState(null);
  const [compOffData, setCompOffData] = useState({ date: "", reason: "" });
  const [compOffLoading, setCompOffLoading] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [leavesRes, typesRes, balanceRes, missingRes, compOffRes] = await Promise.all([
        leaveAPI.getAll(),
        leaveAPI.getTypes(),
        leaveAPI.getBalance(),
        attendanceAPI.getMissing().catch(() => ({ data: [] })),
        compOffAPI.getAll().catch(() => ({ data: [] })),
      ]);
      setLeaves(leavesRes.data);
      setPolicies(typesRes.data);
      setBalance(balanceRes.data);
      setMissingDays(missingRes.data);
      setCompOffs(compOffRes.data);

      if (isManager || isHRAdmin || isSuperAdmin) {
        const usersRes = await userAPI.getAll();
        setUsers(usersRes.data);
      }
    } catch (error) {
      toast.error("Failed to fetch leaves");
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async (e) => {
    e.preventDefault();
    setApplyLoading(true);
    try {
      await leaveAPI.apply({ ...formData, request_type: "leave" });
      toast.success("Leave application submitted");
      setShowApplyDialog(false);
      setFormData({ leave_type: "", start_date: "", end_date: "", reason: "", is_half_day: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply leave");
    } finally {
      setApplyLoading(false);
    }
  };

  const openApproveDialog = (leave, action) => {
    setSelectedLeave({ ...leave, action });
    setApprovalRemarks("");
    setShowApproveDialog(true);
  };

  const handleApproveReject = async () => {
    if (!selectedLeave) return;
    setActionLoading(true);
    try {
      await leaveAPI.action(selectedLeave.id, { action: selectedLeave.action, remarks: approvalRemarks });
      toast.success(`Request ${selectedLeave.action}d successfully`);
      setShowApproveDialog(false);
      setSelectedLeave(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${selectedLeave.action} request`);
    } finally {
      setActionLoading(false);
    }
  };

  const openRegDialog = (date) => {
    setRegData({ date: date || "", resolution: "present", leave_type: "", reason: "" });
    setShowRegDialog(true);
  };

  const handleRegularize = async (e) => {
    e.preventDefault();
    setRegLoading(true);
    try {
      if (regData.resolution === "leave") {
        if (!regData.leave_type) {
          toast.error("Please select a leave type");
          setRegLoading(false);
          return;
        }
        await leaveAPI.apply({
          request_type: "leave",
          leave_type: regData.leave_type,
          start_date: regData.date,
          end_date: regData.date,
          reason: regData.reason,
          is_half_day: false,
        });
      } else {
        await leaveAPI.apply({
          request_type: "regularization",
          regularization_date: regData.date,
          start_date: regData.date,
          end_date: regData.date,
          regularization_type: regData.resolution === "half_day" ? "half_day" : "full_day",
          reason: regData.reason,
        });
      }
      toast.success("Regularization request submitted for approval");
      setShowRegDialog(false);
      setRegData({ date: "", resolution: "present", leave_type: "", reason: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setRegLoading(false);
    }
  };

  const openCompOffDialog = (co) => {
    setSelectedCompOff(co);
    setCompOffData({ date: "", reason: "" });
    setShowCompOffDialog(true);
  };

  const handleAvailCompOff = async (e) => {
    e.preventDefault();
    setCompOffLoading(true);
    try {
      await compOffAPI.avail(selectedCompOff.id, { date: compOffData.date, reason: compOffData.reason });
      toast.success("Comp-off booked successfully");
      setShowCompOffDialog(false);
      setSelectedCompOff(null);
      setCompOffData({ date: "", reason: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to book comp-off");
    } finally {
      setCompOffLoading(false);
    }
  };

  const handleExportCSV = () => {
    const headers = ["Employee ID", "Employee Name", "Type", "Start Date", "End Date", "Days", "Status", "Applied On"];
    const rows = filteredLeaves.map((leave) => {
      const emp = users.find((u) => u.id === leave.user_id);
      return [
        leave.employee_id || emp?.employee_id || "",
        emp ? `${emp.first_name} ${emp.last_name}` : "Self",
        capitalizeFirst(leave.request_type) + (leave.leave_type ? ` (${leave.leave_type})` : ""),
        leave.start_date,
        leave.end_date,
        leave.days,
        capitalizeFirst(leave.status),
        leave.created_at?.split("T")[0] || "",
      ];
    });
    const csvContent = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leaves_report_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Report exported successfully");
  };

  const filteredLeaves = leaves.filter((leave) => {
    if (activeTab === "all") return true;
    return leave.status === activeTab;
  });

  const canApprove = isManager || isHRAdmin || isSuperAdmin;
  const pendingApprovals = leaves.filter((l) => l.status === "pending" && l.user_id !== user?.id).length;
  const availableCompOffs = compOffs.filter((c) => c.status === "available").length;

  const getEmployeeName = (leave) => {
    if (leave.user_id === user?.id) return "You";
    const emp = users.find((u) => u.id === leave.user_id);
    return emp ? `${emp.first_name} ${emp.last_name}` : leave.employee_id || "Employee";
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { className: "bg-amber-100 text-amber-700", icon: Clock },
      manager_approved: { className: "bg-blue-100 text-blue-700", icon: CheckCircle },
      approved: { className: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
      rejected: { className: "bg-red-100 text-red-700", icon: XCircle },
    };
    const v = variants[status] || variants.pending;
    const Icon = v.icon;
    return (
      <Badge className={`${v.className} flex items-center gap-1 w-fit`}>
        <Icon className="w-3 h-3" />
        {capitalizeFirst((status || "").replace(/_/g, " "))}
      </Badge>
    );
  };

  const compOffStatusBadge = (status) => {
    const map = {
      available: "bg-emerald-100 text-emerald-700",
      used: "bg-slate-100 text-slate-600",
      expired: "bg-red-100 text-red-700",
    };
    return <Badge className={`${map[status] || map.available} w-fit`}>{capitalizeFirst(status)}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leaves-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Leave Management</h1>
          <p className="text-slate-500">Apply leaves, regularize attendance and use comp-offs</p>
        </div>
        <div className="flex items-center gap-2">
          {view === "leaves" && canApprove && (
            <Button variant="outline" onClick={handleExportCSV} data-testid="export-leaves-btn">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          )}
          {view === "leaves" && (
            <Dialog open={showApplyDialog} onOpenChange={setShowApplyDialog}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="apply-leave-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Apply Leave
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading">Apply for Leave</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleApply} className="space-y-4 mt-4">
                  <div className="form-group">
                    <Label>Leave Type</Label>
                    <Select value={formData.leave_type} onValueChange={(value) => setFormData({ ...formData, leave_type: value })}>
                      <SelectTrigger data-testid="leave-type-select">
                        <SelectValue placeholder="Select leave type" />
                      </SelectTrigger>
                      <SelectContent>
                        {policies.length > 0 ? (
                          policies.map((policy) => (
                            <SelectItem key={policy.leave_type} value={policy.leave_type}>
                              {capitalizeFirst(policy.leave_type)} ({policy.annual_quota} days)
                            </SelectItem>
                          ))
                        ) : (
                          <>
                            <SelectItem value="casual">Casual Leave</SelectItem>
                            <SelectItem value="sick">Sick Leave</SelectItem>
                            <SelectItem value="earned">Earned Leave</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="form-group">
                      <Label>Start Date</Label>
                      <Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} required data-testid="leave-start-date" />
                    </div>
                    <div className="form-group">
                      <Label>End Date</Label>
                      <Input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} required data-testid="leave-end-date" />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="half_day" checked={formData.is_half_day} onCheckedChange={(checked) => setFormData({ ...formData, is_half_day: checked })} />
                    <Label htmlFor="half_day" className="text-sm font-normal">Half day leave</Label>
                  </div>
                  <div className="form-group">
                    <Label>Reason</Label>
                    <Textarea value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} required rows={3} placeholder="Reason for leave..." data-testid="leave-reason" />
                  </div>
                  <Button type="submit" className="w-full btn-primary" disabled={applyLoading} data-testid="submit-leave">
                    {applyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Application"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Primary View Tabs */}
      <Tabs value={view} onValueChange={setView}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="leaves" data-testid="view-leaves-tab">Leave Requests</TabsTrigger>
          <TabsTrigger value="regularization" data-testid="view-regularization-tab">
            Regularization
            {missingDays.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center rounded-full bg-orange-500 text-white text-[10px] w-4 h-4">{missingDays.length}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="compoff" data-testid="view-compoff-tab">
            Comp-off
            {availableCompOffs > 0 && (
              <span className="ml-2 inline-flex items-center justify-center rounded-full bg-emerald-500 text-white text-[10px] w-4 h-4">{availableCompOffs}</span>
            )}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* ============ LEAVES VIEW ============ */}
      {view === "leaves" && (
        <>
          {balance.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {balance.map((b) => (
                <Card key={b.leave_type} className="stat-card">
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-500 capitalize">{b.leave_type} Leave</p>
                    <div className="flex items-baseline gap-1 mt-1">
                      <span className="text-2xl font-bold text-slate-900">{b.available}</span>
                      <span className="text-slate-400">/ {b.total}</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Used: {b.used}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {canApprove && pendingApprovals > 0 && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4 flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <span className="text-amber-800 font-medium">
                  {pendingApprovals} request{pendingApprovals > 1 ? "s" : ""} pending your approval
                </span>
              </CardContent>
            </Card>
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-slate-100">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="pending">Pending</TabsTrigger>
              <TabsTrigger value="approved">Approved</TabsTrigger>
              <TabsTrigger value="rejected">Rejected</TabsTrigger>
            </TabsList>
          </Tabs>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    {canApprove && <TableHead>Employee</TableHead>}
                    <TableHead>Type</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Days</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Applied On</TableHead>
                    {canApprove && <TableHead>Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeaves.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canApprove ? 8 : 6} className="text-center py-8 text-slate-500">
                        No leave requests found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredLeaves.map((leave) => (
                      <TableRow key={leave.id} data-testid={`leave-row-${leave.id}`}>
                        {canApprove && (
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                                <User className="w-4 h-4 text-slate-600" />
                              </div>
                              <span className="font-medium">{getEmployeeName(leave)}</span>
                            </div>
                          </TableCell>
                        )}
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${leave.request_type === "regularization" ? "bg-orange-100" : "bg-amber-100"}`}>
                              {leave.request_type === "regularization" ? (
                                <AlertTriangle className="w-5 h-5 text-orange-600" />
                              ) : (
                                <CalendarDays className="w-5 h-5 text-amber-600" />
                              )}
                            </div>
                            <div>
                              <span className="font-medium capitalize">
                                {leave.request_type === "regularization" ? "Regularization" : leave.leave_type}
                              </span>
                              {leave.regularization_type && (
                                <p className="text-xs text-slate-500 capitalize">{leave.regularization_type.replace(/_/g, " ")}</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {formatDate(leave.start_date)}
                          {leave.start_date !== leave.end_date && ` - ${formatDate(leave.end_date)}`}
                        </TableCell>
                        <TableCell>{leave.days} {leave.is_half_day ? "(Half)" : ""}</TableCell>
                        <TableCell className="max-w-xs truncate" title={leave.reason}>{leave.reason}</TableCell>
                        <TableCell>{getStatusBadge(leave.status)}</TableCell>
                        <TableCell>{formatDate(leave.created_at)}</TableCell>
                        {canApprove && (
                          <TableCell>
                            {leave.status === "pending" && leave.user_id !== user?.id && (
                              <div className="flex items-center gap-2">
                                <Button size="sm" variant="ghost" className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" onClick={() => openApproveDialog(leave, "approve")} data-testid={`approve-leave-${leave.id}`}>
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                                <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => openApproveDialog(leave, "reject")} data-testid={`reject-leave-${leave.id}`}>
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Reject
                                </Button>
                              </div>
                            )}
                            {leave.status === "manager_approved" && isHRAdmin && (
                              <Button size="sm" variant="ghost" className="text-emerald-600 hover:text-emerald-700" onClick={() => openApproveDialog(leave, "approve")} data-testid={`final-approve-leave-${leave.id}`}>
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Final Approve
                              </Button>
                            )}
                            {leave.approval_remarks && (
                              <p className="text-xs text-slate-500 mt-1" title={leave.approval_remarks}>
                                Remarks: {leave.approval_remarks.substring(0, 30)}
                              </p>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      {/* ============ REGULARIZATION VIEW ============ */}
      {view === "regularization" && (
        <div className="space-y-4" data-testid="regularization-view">
          <Card>
            <CardContent className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <FileEdit className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Missed Attendance Days</p>
                  <p className="text-sm text-slate-500">
                    Working days (last 30 days) where you forgot to mark attendance. Week-offs &amp; holidays are excluded.
                  </p>
                </div>
              </div>
              <Button className="btn-primary" onClick={() => openRegDialog("")} data-testid="new-regularization-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Request
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Day</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {missingDays.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                        No missed attendance days. You&apos;re all caught up!
                      </TableCell>
                    </TableRow>
                  ) : (
                    missingDays.map((m) => (
                      <TableRow key={m.date} data-testid={`missing-row-${m.date}`}>
                        <TableCell className="font-medium">{formatDate(m.date)}</TableCell>
                        <TableCell>{new Date(m.date + "T00:00:00").toLocaleDateString("en-US", { weekday: "long" })}</TableCell>
                        <TableCell className="text-slate-500">{m.reason}</TableCell>
                        <TableCell>
                          <Button size="sm" variant="outline" onClick={() => openRegDialog(m.date)} data-testid={`regularize-${m.date}`}>
                            <FileEdit className="w-4 h-4 mr-1" />
                            Regularize
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <p className="font-medium text-slate-900 mb-2">My Regularization Requests</p>
              <div className="space-y-2">
                {leaves.filter((l) => l.request_type === "regularization" && l.user_id === user?.id).length === 0 ? (
                  <p className="text-sm text-slate-500">No regularization requests submitted yet.</p>
                ) : (
                  leaves
                    .filter((l) => l.request_type === "regularization" && l.user_id === user?.id)
                    .map((l) => (
                      <div key={l.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`reg-request-${l.id}`}>
                        <div>
                          <p className="font-medium text-sm">{formatDate(l.start_date)} · {capitalizeFirst((l.regularization_type || "full_day").replace(/_/g, " "))}</p>
                          <p className="text-xs text-slate-500">{l.reason}</p>
                        </div>
                        {getStatusBadge(l.status)}
                      </div>
                    ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ============ COMP-OFF VIEW ============ */}
      {view === "compoff" && (
        <div className="space-y-4" data-testid="compoff-view">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Gift className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Compensatory Offs</p>
                <p className="text-sm text-slate-500">
                  Earned by working on week-offs or holidays. Use them before they expire.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    {canApprove && <TableHead>Employee</TableHead>}
                    <TableHead>Earned On</TableHead>
                    <TableHead>Expires On</TableHead>
                    <TableHead>Availed On</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {compOffs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canApprove ? 6 : 5} className="text-center py-8 text-slate-500">
                        No comp-offs earned yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    compOffs.map((co) => (
                      <TableRow key={co.id} data-testid={`compoff-row-${co.id}`}>
                        {canApprove && <TableCell className="font-medium">{co.employee_id}</TableCell>}
                        <TableCell className="font-medium">{formatDate(co.earned_date)}</TableCell>
                        <TableCell>{formatDate(co.expiry_date)}</TableCell>
                        <TableCell>{co.availed_date ? formatDate(co.availed_date) : "-"}</TableCell>
                        <TableCell>{compOffStatusBadge(co.status)}</TableCell>
                        <TableCell>
                          {co.status === "available" && co.user_id === user?.id ? (
                            <Button size="sm" variant="outline" onClick={() => openCompOffDialog(co)} data-testid={`avail-compoff-${co.id}`}>
                              <Gift className="w-4 h-4 mr-1" />
                              Avail
                            </Button>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Approve/Reject Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading capitalize">{selectedLeave?.action} Request</DialogTitle>
          </DialogHeader>
          {selectedLeave && (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                <p><span className="text-slate-500">Employee:</span> {getEmployeeName(selectedLeave)}</p>
                <p><span className="text-slate-500">Type:</span> {capitalizeFirst(selectedLeave.leave_type || selectedLeave.request_type)}</p>
                <p><span className="text-slate-500">Duration:</span> {formatDate(selectedLeave.start_date)} - {formatDate(selectedLeave.end_date)}</p>
                <p><span className="text-slate-500">Reason:</span> {selectedLeave.reason}</p>
              </div>
              <div className="form-group">
                <Label>Remarks {selectedLeave.action === "reject" && "(Required)"}</Label>
                <Textarea value={approvalRemarks} onChange={(e) => setApprovalRemarks(e.target.value)} placeholder={`Enter ${selectedLeave.action} remarks...`} rows={3} required={selectedLeave.action === "reject"} data-testid="approval-remarks" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowApproveDialog(false)}>Cancel</Button>
                <Button className={`flex-1 ${selectedLeave.action === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}`} onClick={handleApproveReject} disabled={actionLoading || (selectedLeave.action === "reject" && !approvalRemarks)} data-testid="confirm-action">
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : capitalizeFirst(selectedLeave.action)}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Regularization Dialog */}
      <Dialog open={showRegDialog} onOpenChange={setShowRegDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Regularize Attendance</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRegularize} className="space-y-4 mt-4">
            <div className="form-group">
              <Label>Date</Label>
              <Input type="date" value={regData.date} onChange={(e) => setRegData({ ...regData, date: e.target.value })} required data-testid="reg-date-input" max={new Date().toISOString().split("T")[0]} />
            </div>
            <div className="form-group">
              <Label>How do you want to mark this day?</Label>
              <Select value={regData.resolution} onValueChange={(value) => setRegData({ ...regData, resolution: value })}>
                <SelectTrigger data-testid="reg-resolution-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="present">Mark Present (Full Day)</SelectItem>
                  <SelectItem value="half_day">Mark Half Day</SelectItem>
                  <SelectItem value="leave">Mark as Leave</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {regData.resolution === "leave" && (
              <div className="form-group">
                <Label>Leave Type</Label>
                <Select value={regData.leave_type} onValueChange={(value) => setRegData({ ...regData, leave_type: value })}>
                  <SelectTrigger data-testid="reg-leave-type-select">
                    <SelectValue placeholder="Select leave type" />
                  </SelectTrigger>
                  <SelectContent>
                    {policies.length > 0 ? (
                      policies.map((policy) => (
                        <SelectItem key={policy.leave_type} value={policy.leave_type}>
                          {capitalizeFirst(policy.leave_type)}
                        </SelectItem>
                      ))
                    ) : (
                      <>
                        <SelectItem value="casual">Casual Leave</SelectItem>
                        <SelectItem value="sick">Sick Leave</SelectItem>
                        <SelectItem value="earned">Earned Leave</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="form-group">
              <Label>Reason</Label>
              <Textarea value={regData.reason} onChange={(e) => setRegData({ ...regData, reason: e.target.value })} required rows={3} placeholder="Reason for regularization..." data-testid="reg-reason-input" />
            </div>
            <Button type="submit" className="w-full btn-primary" disabled={regLoading} data-testid="submit-regularization">
              {regLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit for Approval"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Comp-off Avail Dialog */}
      <Dialog open={showCompOffDialog} onOpenChange={setShowCompOffDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Avail Comp-off</DialogTitle>
          </DialogHeader>
          {selectedCompOff && (
            <form onSubmit={handleAvailCompOff} className="space-y-4 mt-4">
              <div className="p-3 bg-emerald-50 rounded-lg text-sm">
                <p>Earned on <span className="font-medium">{formatDate(selectedCompOff.earned_date)}</span></p>
                <p>Valid until <span className="font-medium">{formatDate(selectedCompOff.expiry_date)}</span></p>
              </div>
              <div className="form-group">
                <Label>Day to take off</Label>
                <Input type="date" value={compOffData.date} onChange={(e) => setCompOffData({ ...compOffData, date: e.target.value })} required data-testid="compoff-date-input" min={new Date().toISOString().split("T")[0]} max={selectedCompOff.expiry_date} />
              </div>
              <div className="form-group">
                <Label>Reason (optional)</Label>
                <Textarea value={compOffData.reason} onChange={(e) => setCompOffData({ ...compOffData, reason: e.target.value })} rows={2} placeholder="Reason..." data-testid="compoff-reason-input" />
              </div>
              <Button type="submit" className="w-full btn-primary" disabled={compOffLoading} data-testid="submit-compoff">
                {compOffLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Book Comp-off"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeavesPage;
